import { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { useI18n } from '../../../contexts/I18nContext';

const sortOptions = (list) => {
  const sorted = [...list].sort((a, b) => {
    const sa = typeof a.sort === 'number' ? a.sort : parseInt(a.sort, 10) || 0;
    const sb = typeof b.sort === 'number' ? b.sort : parseInt(b.sort, 10) || 0;
    return sa - sb;
  });
  return sorted
    .map((item) => ({
      value: item.value,
      label: item.label || item.value,
      description: item.description || ''
    }))
    .filter((opt) => opt.value);
};

const pickOptionValue = (current, options) => {
  if (current && options.some((opt) => opt.value === current)) return current;
  return options[0]?.value || '';
};

export const useVideoOptions = () => {
  const { language } = useI18n();
  const [model, setModel] = useState('');
  const [videoMode, setVideoMode] = useState('');
  const [aspectRatio, setAspectRatio] = useState('');
  const [videoResolution, setVideoResolution] = useState('');
  const [videoDuration, setVideoDuration] = useState('');
  const [modelOptions, setModelOptions] = useState([]);
  const [videoModeOptions, setVideoModeOptions] = useState([]);
  const [aspectRatioOptions, setAspectRatioOptions] = useState([]);
  const [videoResolutionOptions, setVideoResolutionOptions] = useState([]);
  const [videoDurationOptions, setVideoDurationOptions] = useState([]);
  const [batchCache, setBatchCache] = useState({});

  const apiLanguage = language === 'en' ? 'en' : language === 'zh-TW' ? 'tc' : 'sc';

  const fetchDictOptions = useCallback(async (dictName) => {
    if (!dictName) return [];
    const response = await api.get('/dictDetail', {
      params: {
        dictName,
        language: apiLanguage,
        page: 0,
        limit: 100
      }
    });
    const list = Array.isArray(response.data?.data) ? response.data.data : [];
    return sortOptions(list);
  }, [apiLanguage]);

  const fetchBatchDicts = useCallback(async (dictNames) => {
    if (!Array.isArray(dictNames) || dictNames.length === 0) return {};
    const response = await api.get('/dictDetails', {
      params: {
        dictNames: dictNames.join(','),
        language: apiLanguage,
        page: 0,
        limit: 100
      }
    });
    const body = response.data || {};
    const result = {};
    Object.keys(body || {}).forEach((name) => {
      const section = body[name];
      const data = Array.isArray(section?.data) ? section.data : [];
      result[name] = sortOptions(data);
    });
    return result;
  }, [apiLanguage]);

  const setModelWithReset = useCallback((nextModel) => {
    setModel(nextModel);
    setVideoMode('');
    setAspectRatio('');
    setVideoResolution('');
    setVideoDuration('');
  }, []);

  useEffect(() => {
    let active = true;
    const loadModels = async () => {
      try {
        const models = await fetchDictOptions('videoModel');
        if (!active) return;
        setModelOptions(models);
        const allModelValues = models.map((m) => m.value).filter(Boolean);
        const dictNames = [];
        for (const mv of allModelValues) {
          dictNames.push(`videoMode_${mv}`);
          dictNames.push(`videoAspectRatio_${mv}`);
          dictNames.push(`VideoResolution_${mv}`);
          dictNames.push(`VideoDuration_${mv}`);
        }
        try {
          const cache = await fetchBatchDicts(dictNames);
          if (!active) return;
          setBatchCache(cache);
        } catch (e) {
          if (!active) return;
          setBatchCache({});
        }
        const nextModel = pickOptionValue(model, models);
        if (nextModel !== model) setModelWithReset(nextModel);
      } catch (e) {
        if (!active) return;
        setModelOptions([]);
      }
    };
    loadModels();
    return () => {
      active = false;
    };
  }, [fetchBatchDicts, fetchDictOptions, model, setModelWithReset]);

  useEffect(() => {
    let active = true;
    const loadDependents = async () => {
      if (!model) {
        setVideoModeOptions([]);
        setAspectRatioOptions([]);
        setVideoResolutionOptions([]);
        setVideoDurationOptions([]);
        return;
      }
      const modes = batchCache[`videoMode_${model}`] || [];
      const ratios = batchCache[`videoAspectRatio_${model}`] || [];
      const resolutions = batchCache[`VideoResolution_${model}`] || [];
      const durations = batchCache[`VideoDuration_${model}`] || [];
      if (!active) return;
      setVideoModeOptions(modes);
      setAspectRatioOptions(ratios);
      setVideoResolutionOptions(resolutions);
      setVideoDurationOptions(durations);

      const nextMode = pickOptionValue(videoMode, modes);
      const nextRatio = pickOptionValue(aspectRatio, ratios);
      const nextResolution = pickOptionValue(videoResolution, resolutions);
      const nextDuration = pickOptionValue(videoDuration, durations);

      if (nextMode !== videoMode) setVideoMode(nextMode);
      if (nextRatio !== aspectRatio) setAspectRatio(nextRatio);
      if (nextResolution !== videoResolution) setVideoResolution(nextResolution);
      if (nextDuration !== videoDuration) setVideoDuration(nextDuration);
    };
    loadDependents();
    return () => {
      active = false;
    };
  }, [model, batchCache, videoMode, aspectRatio, videoResolution, videoDuration]);

  return {
    model,
    setModel: setModelWithReset,
    videoMode,
    setVideoMode,
    aspectRatio,
    setAspectRatio,
    videoResolution,
    setVideoResolution,
    videoDuration,
    setVideoDuration,
    modelOptions,
    videoModeOptions,
    aspectRatioOptions,
    videoResolutionOptions,
    videoDurationOptions
  };
};
